import {
  Badge,
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
  Link,
  Text,
  Tooltip,
} from '@customerio/pluma-components/react';
import plur from 'plur';
import { UsageChips, type UsageChipProps } from '../components/usage-chips';
import type { Conversion } from './adapter';
import { conversionImpact, countedWhen } from './rows';

/**
 * The cells of the Conversions tab.
 *
 * The layout and copy are the spike's (`customerio/ui#16717`,
 * `app/components/data-index-conversions-table`), read from a goal rather than
 * from a definition built in the browser.
 */

export type ConversionNameCellProps = {
  conversion: Conversion;
  /** The goal's own page, which the row opens. */
  href: string;
};

/**
 * A conversion's name, and nothing else.
 *
 * The name is a primary link to the goal, styled like the name in the lists
 * beside this one (`components/name-cell.tsx`) rather than like the spike's
 * underline-less secondary link: a table cell has nothing around it to mark a
 * link by, so the link has to look like one on its own.
 *
 * Whether it is counting is a column of its own ({@link ConversionStatusCell}),
 * and where it came from is a view (`CONVERSION_VIEWS`). Both used to sit
 * inline here, which left the name truncating early to make room for two chips
 * that, on a migrated row, said nearly the same thing — the backfill writes
 * every migrated goal untracked.
 */
export function ConversionNameCell({ conversion, href }: ConversionNameCellProps) {
  return (
    <Link href={href} variant="primary" truncate title={conversion.name}>
      {conversion.name}
    </Link>
  );
}

export type ConversionStatusCellProps = {
  conversion: Conversion;
};

/**
 * Whether the conversion is still counting.
 *
 * Both states are written out rather than only the exceptional one: a column
 * that marks nothing on a tracking row makes an untracked row read as a
 * rendering gap rather than as a state someone chose. Spelling both out is
 * what the column buys — inline beside the name there was only room for "On"
 * and "Off".
 *
 * The label says the state, so the indicator's colour is not the only thing
 * carrying it.
 */
export function ConversionStatusCell({ conversion }: ConversionStatusCellProps) {
  return (
    <Badge color={conversion.isTracking ? 'success' : 'neutral'} showStatusIndicator>
      {conversion.isTracking ? 'Tracking' : 'Not tracking'}
    </Badge>
  );
}

export type ConversionCountedWhenCellProps = {
  conversion: Conversion;
};

/** When the conversion counts, or that its sources do not agree on one answer. */
export function ConversionCountedWhenCell({ conversion }: ConversionCountedWhenCellProps) {
  const when = countedWhen(conversion);

  if (when.kind === 'varies') {
    return (
      // Underlined because the trigger is inline text: the tooltip carries the
      // whole reason the cell says Varies, and nothing else in the cell says
      // there is more to read.
      <Tooltip content="The things using this conversion count it over different windows." placement="top" isUnderlined>
        <Text color="subtle">Varies</Text>
      </Tooltip>
    );
  }

  return <Text color="subtle">{when.text}</Text>;
}

/**
 * How many people converted recently.
 *
 * The count itself is AUD-4576: it needs a read of its own that the list call
 * does not carry, and one round trip per row is not what this column is for.
 * The column is here so the shape of the table is settled, and says plainly
 * that the figure is not in yet rather than showing a zero.
 */
export function ConversionCountCell() {
  return (
    <Text color="subtle" role="img" aria-label="Not available yet">
      —
    </Text>
  );
}

export type ConversionImpactCellProps = {
  conversion: Conversion;
  /** The goal's own page, where a value is set. */
  href: string;
};

/** What a conversion is worth, or a way to say what it is worth. */
export function ConversionImpactCell({ conversion, href }: ConversionImpactCellProps) {
  const impact = conversionImpact(conversion);

  if (impact.kind === 'value') {
    return <Text>{impact.text}</Text>;
  }

  if (impact.kind === 'attribute') {
    return (
      // Underlined for the same reason as Varies: the attribute the value is
      // read from is only in the tooltip.
      <Tooltip
        content={
          impact.attribute == null
            ? 'Each conversion is worth the value on the event that triggered it.'
            : `Each conversion is worth the "${impact.attribute}" attribute of the event that triggered it.`
        }
        placement="top"
        isUnderlined
      >
        <Text color="subtle">From event attribute</Text>
      </Tooltip>
    );
  }

  return (
    <Link href={href} variant="primary">
      Add value
    </Link>
  );
}

export type ConversionUsageTerms = {
  /** What the workspace calls a campaign, singular. */
  campaignSingular: string;
  /** What the workspace calls a broadcast, singular. */
  newsletterSingular: string;
};

export type ConversionUsageCellProps = {
  conversion: Conversion;
  terms: ConversionUsageTerms;
};

const LIST = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });

/** A surface a conversion is used by, and what the workspace calls one of them. */
type NamedUsageChip = UsageChipProps & { noun: string };

/**
 * What uses a conversion, counted by kind.
 *
 * Only the three surfaces that carry conversion settings can use one, which
 * is why this counts three things where the attribute and event rows beside
 * it count seven.
 */
export function ConversionUsageCell({ conversion, terms }: ConversionUsageCellProps) {
  const chips = (
    [
      { icon: 'campaigns', count: conversion.usage.campaigns, noun: terms.campaignSingular },
      { icon: 'newsletter', count: conversion.usage.newsletters, noun: terms.newsletterSingular },
      { icon: 'transactional-api', count: conversion.usage.transactional, noun: 'transactional message' },
    ] satisfies NamedUsageChip[]
  ).filter((chip) => chip.count > 0);

  if (chips.length === 0) {
    return <Text color="subtle">-</Text>;
  }

  const tooltip = LIST.format(chips.map((chip) => `${String(chip.count)} ${plur(chip.noun, chip.count)}`));

  return <UsageChips chips={chips} tooltip={tooltip} />;
}

export type ConversionsCountNoteProps = {
  /** How many days the count column covers. */
  days: number;
};

/** What the count column counts, which the column header has no room to say. */
export function ConversionsCountNote({ days }: ConversionsCountNoteProps) {
  return (
    <Text color="subtle">
      {`Conversion counts for the last ${String(days)} ${plur('day', days)} will be available soon.`}
    </Text>
  );
}

/**
 * A list that could not be read.
 *
 * Kept apart from the empty state: a workspace with no conversions and a
 * workspace whose conversions failed to load look identical in an empty
 * table, and only one of them should be told to try again.
 */
export function ConversionsLoadError() {
  return (
    <EmptyState>
      <EmptyStateTitle>Conversions could not be loaded</EmptyStateTitle>
      <EmptyStateDescription>Reload the page to try again.</EmptyStateDescription>
    </EmptyState>
  );
}
